/**
 * StorageService.ts
 *
 * A service to handle all localStorage operations for the stopwatch app.
 * This centralizes data persistence logic and provides error handling.
 */

import { WorkoutSession } from '../types';
import {
  safeSetItem,
  safeGetItem,
  STORAGE_KEYS,
  checkDataVersion,
} from '../utils/persistenceUtils';

/**
 * Validate workout session data structure
 * @param session - Session object to validate
 * @returns True if valid, false if invalid
 */
const isValidWorkoutSession = (session: unknown): boolean => {
  if (!session || typeof session !== 'object') {
    return false;
  }

  const s = session as Partial<WorkoutSession>;

  // Check for required properties with proper typeguards
  return (
    typeof s.id !== 'undefined' &&
    (typeof s.date === 'string' || s.date instanceof Date) &&
    Array.isArray(s.laps) &&
    s.segments !== undefined &&
    typeof s.segments === 'object'
  );
};

// Workout Storage Functions

/**
 * Save workout sessions to localStorage with validation
 * @param sessions - Array of workout session objects
 * @returns True if successful, false if validation failed or error occurred
 */
export const saveWorkoutSessions = (sessions: WorkoutSession[]): boolean => {
  // Validate input
  if (!sessions || !Array.isArray(sessions) || sessions.length === 0) {
    console.warn('Invalid workout sessions data provided to saveWorkoutSessions');
    return false;
  }

  // Validate each session
  const validSessions = sessions.filter(isValidWorkoutSession);

  if (validSessions.length !== sessions.length) {
    console.warn(
      `${sessions.length - validSessions.length} invalid session(s) removed during save`
    );
  }

  if (validSessions.length === 0) {
    console.warn('No valid workout sessions to save');
    return false;
  }

  // Check data version
  checkDataVersion();

  // Save the valid sessions
  return safeSetItem(STORAGE_KEYS.WORKOUT_SESSIONS, JSON.stringify(validSessions));
};

/**
 * Load workout sessions from localStorage with error handling and validation
 * @returns Array of workout sessions or empty array if none found/error
 */
export const loadWorkoutSessions = (): WorkoutSession[] => {
  try {
    // Check data version
    checkDataVersion();

    const savedSessions = safeGetItem(STORAGE_KEYS.WORKOUT_SESSIONS);
    if (!savedSessions) {
      return [];
    }

    const parsedSessions = JSON.parse(savedSessions);

    // Validate sessions
    if (!Array.isArray(parsedSessions)) {
      console.error('Workout sessions data is not an array');
      return [];
    }

    const validSessions = parsedSessions.filter(isValidWorkoutSession);

    if (validSessions.length !== parsedSessions.length) {
      console.warn(
        `${parsedSessions.length - validSessions.length} invalid session(s) removed during load`
      );
      // Save the cleaned up data back to storage
      saveWorkoutSessions(validSessions);
    }

    return validSessions;
  } catch (error) {
    console.error('Error loading saved workout sessions:', error);
    return [];
  }
};

/**
 * Get a single workout session by ID
 * @param sessionId - The session ID to retrieve
 * @returns The session object or null if not found
 */
export const getWorkoutSession = (sessionId: string | number): WorkoutSession | null => {
  const sessions = loadWorkoutSessions();
  return sessions.find(session => session.id === sessionId) || null;
};

/**
 * Save a single workout session (adds or updates)
 * @param session - The session object to save
 * @returns True if successful, false otherwise
 */
export const saveWorkoutSession = (session: WorkoutSession): boolean => {
  if (!isValidWorkoutSession(session)) {
    console.warn('Invalid workout session provided to saveWorkoutSession');
    return false;
  }

  const sessions = loadWorkoutSessions();
  const existingIndex = sessions.findIndex(s => s.id === session.id);

  if (existingIndex >= 0) {
    // Update existing session
    sessions[existingIndex] = session;
  } else {
    // Add new session
    sessions.push(session);
  }

  return saveWorkoutSessions(sessions);
};

/**
 * Delete a workout session by ID
 * @param sessionId - The session ID to delete
 * @returns True if successful, false if not found or error
 */
export const deleteWorkoutSession = (sessionId: string | number): boolean => {
  const sessions = loadWorkoutSessions();
  const initialLength = sessions.length;

  const filteredSessions = sessions.filter(session => session.id !== sessionId);

  if (filteredSessions.length === initialLength) {
    // Session not found
    return false;
  }

  return saveWorkoutSessions(filteredSessions);
};
